import { useCallback, useEffect, useMemo } from "react";
import { FlatList, View, StyleSheet, Text, Platform, TouchableOpacity, Pressable, ScrollView } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { FontAwesomeIcon } from "../platform";
import {
  faLocationDot,
  faFilter,
  faArrowUpAZ,
  faArrowUpZA,
  faArrowUpWideShort,
  faArrowDownWideShort,
} from "@fortawesome/free-solid-svg-icons";
import Search from "../components/Search";
import ParksStore from "../storage/parksStore";
import { COLORS } from "../theme/colors";

const iconSize = Platform.OS === "web" ? "md" : 14;

/**
 * ParksList component displays a list of parks with sorting and filtering options.
 * Uses Zustand for state management and React Navigation for navigation.
 * @component
 */
export default function ParksList() {
  const navigation = useNavigation();
  const parks = ParksStore((state) => state.parks);
  const amenitiesText = ParksStore((state) => state.amenitiesText);
  const amenities = ParksStore((state) => state.amenities);
  const setAmenitiesText = ParksStore((state) => state.setAmenitiesText);
  const searchText = ParksStore((state) => state.searchText);
  const setSearchText = ParksStore((state) => state.setSearchText);
  const clearSearchText = ParksStore((state) => state.clearSearchText);
  const sortBy = ParksStore((state) => state.sortBy);
  const sortParks = ParksStore((state) => state.sortParks);
  const setDistance = ParksStore((state) => state.setDistance);

  useEffect(() => {
    if (parks && parks.length > 0 && parks[0].distance === undefined) {
      setDistance();
    }
  }, [parks, setDistance]);

  useEffect(() => {
    setAmenitiesText();
  }, [amenities, setAmenitiesText]);

  /**
   * Toggles sorting by park name between ascending and descending.
   * @function
   */
  const sortByName = useCallback(() => {
    const newSort = sortBy === "name-ascending" ? "name-descending" : "name-ascending";
    sortParks(newSort);
  }, [sortBy, sortParks]);

  /**
   * Toggles sorting by park distance between ascending and descending.
   * @function
   */
  const sortByDistance = useCallback(() => {
    const newSort = sortBy === "distance-ascending" ? "distance-descending" : "distance-ascending";
    sortParks(newSort);
  }, [sortBy, sortParks]);

  useEffect(() => {
    setSearchText(searchText);
  }, [searchText, setSearchText]);

  const onSearch = useCallback(
    (text) => {
      setSearchText(text);
    },
    [setSearchText]
  );

  const onClear = useCallback(() => {
    clearSearchText();
  }, [clearSearchText]);

  /**
   * Memoized header component for the parks list, including search and filter controls.
   * @type {React.ReactNode}
   */
  const headerComponent = useMemo(
    () => (
      <View style={styles.headerContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.headerButtonsContainer}
        >
          <View style={styles.headerButtonsRightAlign}>
            {Array.isArray(parks) && parks.length > 0 && parks?.[0]?.distance !== undefined && (
              <Pressable
                onPress={() => sortByDistance()}
                accessibilityRole="button"
                accessibilityLabel="Sort by distance"
                style={({ pressed }) =>
                  StyleSheet.flatten([
                    styles.headerButtonWrapper,
                    styles.filterButtons,
                    {
                      borderColor: COLORS.primary,
                      backgroundColor: COLORS.primaryVariant,
                      opacity: pressed ? 0.4 : 1, // optional visual feedback
                    },
                  ])
                }
              >
                <FontAwesomeIcon
                  icon={sortBy === "distance-descending" ? faArrowDownWideShort : faArrowUpWideShort}
                  size={iconSize}
                  color={COLORS.primary}
                />
                <Text
                  style={StyleSheet.flatten([
                    styles.headerButtonText,
                    {
                      fontWeight: sortBy?.includes("distance") ? "bold" : "normal",
                    },
                  ])}
                >
                  Distance
                </Text>
              </Pressable>
            )}
            <Pressable
              onPress={() => sortByName()}
              accessibilityRole="button"
              accessibilityLabel="Sort by name"
              style={({ pressed }) =>
                StyleSheet.flatten([
                  styles.headerButtonWrapper,
                  styles.filterButtons,
                  {
                    borderColor: COLORS.primary,
                    backgroundColor: COLORS.primaryVariant,
                    opacity: pressed ? 0.4 : 1, // optional visual feedback
                  },
                ])
              }
            >
              <FontAwesomeIcon
                icon={sortBy === "name-descending" ? faArrowUpZA : faArrowUpAZ}
                size={iconSize}
                color={COLORS.primary}
              />
              <Text
                style={StyleSheet.flatten([
                  styles.headerButtonText,
                  { fontWeight: sortBy?.includes("name") ? "bold" : "normal" },
                ])}
              >
                Name
              </Text>
            </Pressable>
            <Pressable
              onPress={() => navigation.navigate("Filter")}
              accessibilityRole="button"
              accessibilityLabel={`Filtering by ${amenitiesText}`}
              style={({ pressed }) =>
                StyleSheet.flatten([
                  styles.headerButtonWrapper,
                  styles.filterButtons,
                  {
                    borderColor: COLORS.secondary,
                    backgroundColor: COLORS.secondaryVariant,
                    opacity: pressed ? 0.4 : 1, // pressed-state feedback
                  },
                ])
              }
            >
              <FontAwesomeIcon icon={faFilter} size={iconSize} color={COLORS.secondary} />
              <Text
                style={StyleSheet.flatten([
                  styles.headerButtonText,
                  { fontWeight: amenitiesText ? "bold" : "normal" }, // bold if filtering active
                ])}
              >
                {amenitiesText}
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </View>
    ),
    [parks, amenitiesText, navigation, sortBy, sortByDistance, sortByName]
  );

  /**
   * Handles navigation to the ParkDetails screen for a selected park.
   * @function
   * @param {object} park - The park object to view details for.
   */
  const handleListItemPress = useCallback(
    (park) => {
      navigation.navigate("ParkDetails", { park_name: park.title });
    },
    [navigation]
  );

  /**
   * Renders a single park item in the list.
   * @function
   * @param {object} param0 - The item and index for the FlatList renderItem prop.
   * @param {object} param0.item - The park object to render.
   * @param {number} param0.index - The index of the item in the list.
   * @returns {React.ReactElement}
   */
  const renderItem = useCallback(
    ({ item, index }) => (
      <TouchableOpacity
        style={styles.itemContainer}
        onPress={() => handleListItemPress(item)}
        accessibilityRole="button"
        accessibilityLabel={`View details for ${item.title}`}
      >
        <View style={styles.itemDetailsContainer}>
          <Text style={styles.title}>{item.title}</Text>
          <Text style={styles.address}>{item.address}</Text>
          {item?.distance && (
            <View style={styles.chipsContainer}>
              <View
                style={StyleSheet.flatten([
                  styles.chip,
                  { backgroundColor: COLORS.tertiaryVariant, borderColor: COLORS.tertiary },
                ])}
              >
                <FontAwesomeIcon icon={faLocationDot} size={iconSize} color={COLORS.tertiary} />
                <Text style={styles.chipText}>{item.distance} miles away</Text>
              </View>
            </View>
          )}
        </View>
      </TouchableOpacity>
    ),
    [handleListItemPress]
  );

  const ParksFlatList = () => (
    <FlatList
      data={parks}
      keyExtractor={(item) => `${item.ID}-${item.title}`}
      ListHeaderComponent={headerComponent}
      renderItem={renderItem}
      contentContainerStyle={styles.listContent}
      initialNumToRender={10}
      testID="parks-list"
    />
  );

  return (
    <View style={styles.mainItemContainer}>
      <Search
        placeholder="Search Parks"
        accessibilityLabel="Search Parks"
        showLeftIcon={true}
        showRightIcon={searchText?.length > 0}
        onTextChange={onSearch}
        onRightIconPress={onClear}
        inputValue={searchText}
      />
      {parks && Array.isArray(parks) && parks.length > 0 ? (
        <ParksFlatList />
      ) : (
        <Text style={{ padding: 20 }}>No parks found.</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  listContent: {
    paddingTop: 5,
    paddingHorizontal: 20,
    paddingBottom: 25,
    backgroundColor: COLORS.tertiaryVariant,
  },
  mainItemContainer: {
    paddingTop: 5,
    flex: 1,
    backgroundColor: COLORS.tertiaryVariant,
  },
  itemContainer: {
    marginBottom: 15,
    backgroundColor: COLORS.background,
    borderRadius: 4,
    borderWidth: 0.5,
  },
  itemDetailsContainer: {
    padding: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    paddingVertical: 4,
  },
  address: {
    fontSize: 14,
    paddingVertical: 4,
  },
  chip: {
    flexDirection: "row",
    marginRight: 10,
    borderRadius: 4,
    borderWidth: 1,
    padding: 8,
    gap: 4,
    marginTop: 8,
    marginBottom: 4,
    alignSelf: "flex-start",
  },
  chipText: {
    fontSize: 12,
  },
  headerContainer: {
    paddingBottom: 10,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderColor: COLORS.tertiary,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === "android" ? 0 : 8,
    marginBottom: 4,
  },
  icon: {
    marginRight: 8,
    color: COLORS.tertiary,
  },
  headerButtonsContainer: {
    flexGrow: 1,
  },
  headerButtonsRightAlign: {
    flexDirection: "row",
    gap: 10,
    marginLeft: "auto",
  },
  headerButtonWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
  },
  headerButtonText: {
    fontSize: 14,
  },
  filterButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
});
